<template>
  <div
    class="relative mb-16 w-full overflow-hidden md:mb-0 md:block md:w-full md:min-w-[337px] md:max-w-[498px] md:overflow-visible"
  >
    <ReviewPhotosGuidanceDrawer
      v-if="defaultImageUrl"
      :grades="gradesWithPictures"
      :initial-grade="selectedGradeLabel ?? gradesWithPictures[0]?.gradeLabel"
      :target-grade
      :target-picture-index
      name="review-photos-guidance-drawer"
      :title="i18n(translations.drawerTitle)"
    >
      <template #trigger="{ open }">
        <!-- Mobile: GalleryNew-style carousel (big primary + scrollable thumbnails) -->
        <div class="md:hidden">
          <div
            class="w-[calc(100vw-48px)] max-w-full overflow-hidden"
            :data-two-images="mobileItems.length === 2 || undefined"
            data-test="review-photos-guidance-pair"
          >
            <GalleryCarousel
              :items="mobileItems.length > 1 ? mobileItems : [mobileItems[0]]"
              primary-image-wrapper-classes="pl-6 w-[max(calc(80vw-24px),214px)]"
              :product-name="trackingModel"
              :secondary-list-classes="
                mobileItems.length === 2
                  ? '!grid-flow-col !auto-cols-[max(calc(80vw-24px),214px)]'
                  : `${CAROUSEL_MAX_HEIGHT_CLASS} ![grid-template-rows:repeat(2,auto)] !pr-24`
              "
            >
              <template #first="{ items }">
                <div
                  class="relative aspect-square w-full cursor-pointer"
                  role="button"
                  tabindex="0"
                  @click="handleReviewClick(open, 0)"
                  @keydown.enter.prevent="handleReviewClick(open, 0)"
                >
                  <RevIllustration
                    v-if="items[0]?.src"
                    alt=""
                    class="rounded-lg h-full w-full object-cover"
                    :height="498"
                    :src="items[0].src"
                    :width="498"
                  />
                  <div class="absolute inset-x-0 bottom-0 p-8">
                    <RevTag
                      class="bg-overlay-low text-static-light-hi"
                      :label="exampleTag"
                      variant="secondary"
                    />
                  </div>
                </div>
              </template>
              <template #rest="{ items }">
                <template v-if="isPendingReviewsPerGrade">
                  <li
                    v-for="n in 4"
                    :key="`skeleton-${n}`"
                    class="shrink-0 [grid-row:span_1] [grid-column:span_1]"
                    :class="SMALL_ALT_ITEM_SIZE_CLASS"
                  >
                    <RevSkeleton
                      height="130px"
                      shape="rectangle"
                      width="130px"
                    />
                  </li>
                  <li aria-hidden="true" class="w-[80px] shrink-0" />
                </template>
                <template v-else>
                  <li
                    v-for="(item, index) in items"
                    v-show="index !== 0"
                    :key="item.id || item.src"
                    class="shrink-0 cursor-pointer [grid-row:span_1] [grid-column:span_1] md:[grid-row:initial] md:[grid-column:initial]"
                    :class="
                      isTwoImages
                        ? 'w-[max(calc(80vw-24px),214px)]'
                        : SMALL_ALT_ITEM_SIZE_CLASS
                    "
                    role="button"
                    tabindex="0"
                    @click="handleReviewClick(open, index)"
                    @keydown.enter.prevent="handleReviewClick(open, index)"
                  >
                    <div
                      class="rounded-lg relative aspect-square h-full w-full overflow-hidden"
                      data-test="review-photos-guidance-slide"
                    >
                      <RevIllustration
                        :alt="item.alt || ''"
                        class="rounded-lg h-full w-full object-cover"
                        :height="isTwoImages ? 498 : 130"
                        :src="item.src"
                        :width="isTwoImages ? 498 : 130"
                      />
                      <div
                        class="rounded-lg absolute inset-0 flex flex-col justify-between p-8"
                      >
                        <RevTag
                          v-if="item.customerName"
                          class="self-start"
                          :label="item.customerName"
                          variant="secondary"
                        />
                        <span
                          v-if="item.reviewDate"
                          class="text-static-light-hi body-1-bold [text-shadow:0_0_4px_rgba(0,0,0,0.5)]"
                        >
                          {{ item.reviewDate }}
                        </span>
                      </div>
                    </div>
                  </li>
                  <li
                    v-if="!isTwoImages"
                    aria-hidden="true"
                    class="w-[80px] shrink-0 md:hidden"
                  />
                </template>
              </template>
            </GalleryCarousel>
          </div>
        </div>

        <!-- Desktop: default image + RevCardCarousel -->
        <div class="hidden md:block">
          <div
            class="rounded-lg relative flex w-full aspect-square cursor-pointer overflow-hidden md:rounded-[32px]"
            data-test="review-photos-guidance-default-image"
            role="button"
            tabindex="0"
            @click="handleReviewClick(open, 0)"
            @keydown.enter.prevent="handleReviewClick(open, 0)"
          >
            <RevIllustration
              alt=""
              class="rounded-lg h-full w-full object-cover md:rounded-[32px]"
              :height="498"
              :src="defaultImageUrl"
              :width="498"
            />
            <div class="absolute inset-x-0 bottom-0 p-16">
              <RevTag
                class="bg-overlay-low text-static-light-hi"
                :label="exampleTag"
                variant="secondary"
              />
            </div>
          </div>

          <div
            v-if="hasReviewsWithAttachments && reviewSlides.length > 0"
            class="bg-surface-default-hi mt-16 rounded-lg p-16 md:rounded-[32px] md:p-24"
          >
            <RevCardCarousel
              :center="false"
              desktop-card-width="narrow"
              mobile-card-width="narrow"
              :next-alternative-text="i18n(translations.alternativeNext)"
              :prev-alternative-text="i18n(translations.alternativePrevious)"
              size="extra-small"
              :subtitle="i18n(translations.subtitle)"
              :title="carouselTitle"
            >
              <div
                v-for="(slide, position) in reviewSlides"
                :key="slide.id"
                class="h-full"
              >
                <RevCard
                  class="h-full cursor-pointer overflow-hidden hover:drop-shadow-xl"
                  @click="handleReviewClick(open, position + 1)"
                >
                  <div
                    class="rounded-lg relative flex w-full aspect-square overflow-hidden"
                    data-test="review-photos-guidance-slide"
                  >
                    <RevIllustration
                      :alt="slide.title"
                      class="rounded-lg h-full w-full object-cover"
                      :height="130"
                      :src="slide.imageUrl"
                      :width="130"
                    />

                    <div
                      class="rounded-lg absolute inset-0 flex flex-col justify-between p-8"
                    >
                      <RevTag
                        v-if="slide.customerName"
                        class="self-start"
                        :label="slide.customerName"
                        variant="secondary"
                      />
                      <span
                        v-if="slide.reviewDate"
                        class="text-static-light-hi body-2-bold [text-shadow:0_0_4px_rgba(0,0,0,0.5)]"
                      >
                        {{ slide.reviewDate }}
                      </span>
                    </div>
                  </div>
                </RevCard>
              </div>
            </RevCardCarousel>
          </div>

          <div
            v-else-if="hasReviewsWithAttachments"
            class="bg-surface-default-hi mt-16 rounded-lg p-16 md:rounded-[32px] md:p-24"
            data-test="review-photos-guidance-no-reviews-card"
          >
            <RevCard class="h-full overflow-hidden">
              <div class="flex flex-col gap-8 p-16">
                <p class="body-2-bold">{{ noReviewsCardTitle }}</p>
                <p class="text-static-default-low caption">
                  {{ i18n(translations.noReviewsCardDescription) }}
                </p>
              </div>
            </RevCard>
          </div>
        </div>
      </template>
    </ReviewPhotosGuidanceDrawer>

    <div
      v-else-if="isPendingReviewsPerGrade"
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <RevSpinner size="large" />
    </div>

    <div
      v-if="isPendingReviewsPerGrade"
      class="bg-surface-default-hi mt-16 hidden rounded-lg p-16 md:rounded-[32px] md:block md:p-24"
    >
      <RevSkeleton
        class="mb-16"
        height="24px"
        shape="rectangle"
        width="220px"
      />
      <div class="flex gap-8">
        <RevSkeleton
          v-for="n in 4"
          :key="n"
          height="130px"
          shape="rectangle"
          width="130px"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useState } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevCard } from '@ds/components/Card'
import { RevCardCarousel } from '@ds/components/CardCarousel'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag } from '@ds/components/Tag'

import {
  CAROUSEL_MAX_HEIGHT_CLASS,
  SMALL_ALT_ITEM_SIZE_CLASS,
} from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.constants'
import type { GalleryImage } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import GalleryCarousel from '~/scopes/product-core/components/presentationals/GalleryNew/components/Carousel/Carousel.vue'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import {
  BACKBOX_GRADE_LABELS,
  type BackboxGradeLabel,
} from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.constants'
import type { ReviewsPerGrade } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.types'
import type { DisplayedReviewWithAttachments } from '~/scopes/reviews/reviews-display/models/reviews'

import {
  DEFAULT_GRADE_IMAGE_CATEGORY,
  GRADE_DEFAULT_IMAGES_BY_CATEGORY,
  GRADE_LABEL_BY_VALUE,
} from './ReviewPhotosGuidance.constants'
import translations from './ReviewPhotosGuidance.translations'
import type {
  FormattedReview,
  GradePictures,
  PictureItem,
  ReviewSlide,
} from './ReviewPhotosGuidanceDrawer.types'
import ReviewPhotosGuidanceDrawer from './ReviewPhotosGuidanceDrawer.vue'

const props = withDefaults(
  defineProps<{
    trackingModel?: string
    categoryId?: string
    reviewsPerGrade?: ReviewsPerGrade | null
    isPendingReviewsPerGrade?: boolean
  }>(),
  {
    trackingModel: '',
    categoryId: undefined,
    reviewsPerGrade: null,
    isPendingReviewsPerGrade: false,
  },
)

const route = useRoute()
const { grade } = useUrlParams()
const i18n = useI18n()
const locale = useI18nLocale()
const { trackClick } = useTracking()

const targetGrade = useState<BackboxGradeLabel | null>(
  'review-photos-guidance-target-grade',
  () => null,
)
const targetPictureIndex = useState<number>(
  'review-photos-guidance-target-picture-index',
  () => 0,
)

const selectedGradeLabel = computed<BackboxGradeLabel | undefined>(() => {
  if (grade.value === undefined || grade.value === null) return undefined

  return GRADE_LABEL_BY_VALUE[grade.value]
})

const gradeDefaultImages = computed<
  Record<BackboxGradeLabel, string> | undefined
>(() => {
  return (
    GRADE_DEFAULT_IMAGES_BY_CATEGORY[
      props.categoryId ?? DEFAULT_GRADE_IMAGE_CATEGORY
    ] ?? GRADE_DEFAULT_IMAGES_BY_CATEGORY[DEFAULT_GRADE_IMAGE_CATEGORY]
  )
})

const defaultImageUrl = computed<string | null>(() => {
  if (!selectedGradeLabel.value) return null

  return gradeDefaultImages.value?.[selectedGradeLabel.value] ?? null
})

const GRADE_NAME_BY_LABEL: Record<BackboxGradeLabel, string> = {
  PREMIUM: i18n(translations.tabPremium),
  FLAWLESS: i18n(translations.tabFlawless),
  VERY_GOOD: i18n(translations.tabVeryGood),
  ECO: i18n(translations.tabEco),
}

const gradeName = computed(() =>
  selectedGradeLabel.value ? GRADE_NAME_BY_LABEL[selectedGradeLabel.value] : '',
)

const carouselTitle = computed(() =>
  selectedGradeLabel.value
    ? i18n(translations.gradeTitle, { grade: gradeName.value })
    : '',
)

const exampleTag = computed(() =>
  selectedGradeLabel.value
    ? i18n(translations.exampleTag, { grade: gradeName.value })
    : '',
)

const noReviewsCardTitle = computed(() =>
  selectedGradeLabel.value
    ? i18n(translations.noReviewsCardTitle, { grade: gradeName.value })
    : '',
)

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(locale, {
    month: 'short',
    year: 'numeric',
  })
}

function toFormattedReview(
  review: DisplayedReviewWithAttachments,
): FormattedReview {
  return {
    ...review,
    reviewDate: review.createdAt,
    purchaseDate: review.product.purchasedAt,
    pictures: review.attachments.map(({ url }) => url),
  }
}

const MAX_REVIEW_PICTURES = 20

/**
 * Selects up to MAX_REVIEW_PICTURES pictures across reviews using a
 * round-robin strategy: the first picture of every review is picked before
 * any second picture, and so on. This maximizes the number of distinct
 * reviews shown while still allowing multiple pictures from the same review
 * when there aren't enough reviews to reach the limit. Images from the same
 * review are kept together in the final output, preserving review order.
 */
function selectReviewPictures(
  reviews: DisplayedReviewWithAttachments[],
): PictureItem[] {
  const reviewsWithAttachments = reviews.filter(
    (review) => review.attachments && review.attachments.length > 0,
  )

  const selectedByReview: PictureItem[][] = reviewsWithAttachments.map(() => [])
  const attachmentCounts = reviewsWithAttachments.map(
    (review) => review.attachments.length,
  )

  let totalSelected = 0
  let attachmentIndex = 0

  while (totalSelected < MAX_REVIEW_PICTURES) {
    let addedThisRound = false

    for (let i = 0; i < reviewsWithAttachments.length; i += 1) {
      if (totalSelected >= MAX_REVIEW_PICTURES) break
      if (attachmentIndex >= attachmentCounts[i]) continue

      const review = reviewsWithAttachments[i]
      const attachment = review.attachments[attachmentIndex]
      const formatted = toFormattedReview(review)

      selectedByReview[i].push({
        id: `${review.id}-${attachmentIndex}`,
        url: attachment.url,
        review: formatted,
      })

      totalSelected += 1
      addedThisRound = true
    }

    if (!addedThisRound) break

    attachmentIndex += 1
  }

  return selectedByReview.flat()
}

/**
 * All grade labels, each with a flat list of pictures. Every grade starts
 * with the default grade image as the first slide (index 0), followed by
 * review photos. The default image slide has `review: null` so the right
 * side can show a default content card instead of a review.
 */
const gradesWithPictures = computed<GradePictures[]>(() => {
  if (!props.reviewsPerGrade) return []

  return BACKBOX_GRADE_LABELS.flatMap((gradeLabel) => {
    const defaultImage = gradeDefaultImages.value?.[gradeLabel]
    if (!defaultImage) return []

    const defaultPicture: PictureItem = {
      id: `default-${gradeLabel}`,
      url: defaultImage,
      review: null,
    }

    const reviews = props.reviewsPerGrade?.[gradeLabel] ?? []

    const reviewPictures = selectReviewPictures(
      reviews as DisplayedReviewWithAttachments[],
    )

    return [{ gradeLabel, pictures: [defaultPicture, ...reviewPictures] }]
  })
})

const hasReviewsWithAttachments = computed(
  () => gradesWithPictures.value.length > 0,
)

const selectedGradePictures = computed<GradePictures | undefined>(() => {
  return gradesWithPictures.value.find(
    ({ gradeLabel }) => gradeLabel === selectedGradeLabel.value,
  )
})

const reviewSlides = computed<ReviewSlide[]>(() => {
  const gradePictures = selectedGradePictures.value
  if (!gradePictures) return []

  return gradePictures.pictures
    .map((picture, index) => ({ picture, index }))
    .filter(({ picture }) => picture.review !== null)
    .map(({ picture, index }) => {
      const review = picture.review as FormattedReview
      const firstName = review.customer?.firstName ?? null
      const lastName = review.customer?.lastName ?? null
      const customerName =
        firstName || lastName
          ? [firstName, lastName].filter(Boolean).join(' ')
          : null

      return {
        id: picture.id,
        imageUrl: picture.url,
        title: review.product.title,
        customerName,
        reviewDate: review.createdAt ? formatDate(review.createdAt) : null,
        gradeLabel: gradePictures.gradeLabel,
        pictureIndex: index,
      }
    })
})

function handleReviewClick(openModal: () => void, index: number) {
  if (index === 0) {
    const gradeLabel =
      selectedGradeLabel.value ?? gradesWithPictures.value[0]?.gradeLabel

    if (gradeLabel) {
      targetGrade.value = gradeLabel
      targetPictureIndex.value = 0
    }
    openModal()

    return
  }

  const slide = reviewSlides.value[index - 1]
  if (!slide) return

  targetGrade.value = slide.gradeLabel
  targetPictureIndex.value = slide.pictureIndex
  openModal()

  trackClick(
    removeEmptyValuesInObject({
      page_type: route.name as string,
      position: index,
      product_model: props.trackingModel || slide.imageUrl,
      zone: 'review_photo_guidance',
    }),
  )
}

const mobileItems = computed<
  (GalleryImage & {
    customerName?: string | null
    reviewDate?: string | null
  })[]
>(() => {
  if (!defaultImageUrl.value) return []

  const items: (GalleryImage & {
    customerName?: string | null
    reviewDate?: string | null
  })[] = [
    {
      id: 'default-grade-image',
      src: defaultImageUrl.value,
      alt: '',
      customerName: null,
      reviewDate: null,
    },
  ]

  return [
    ...items,
    ...reviewSlides.value.map((slide) => ({
      id: slide.id,
      src: slide.imageUrl,
      alt: slide.title,
      customerName: slide.customerName,
      reviewDate: slide.reviewDate,
    })),
  ]
})

const isTwoImages = computed(() => mobileItems.value.length === 2)
</script>
